import { Input, Modal, Radio, message } from 'antd';
import { FC, useEffect, useRef, useState } from 'react';
import { AddButton } from '../../../../../lib/components/Buttons/AddButton';
import { InputItem } from '../../../../../lib/components/InputItem';
import { isValidCpf, maskCpf, sanitizeCpf } from '../../../../../lib/utils/cpfValidator';
import { InsertSearchTagDto } from '../dto/SearchTagDto';
import {
	createSearchTag,
	findSearchTagByCpf,
	submitWithOverwriteConfirm,
} from '../hooks/SearchTagsApi';

interface IProps {
	visible: boolean;
	close: () => void;
	insert_success: () => void;
	prefix: string;
	tag_name_in_portuguese: string;
}

const TAG_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9\s.,\/ºª°-]+$/u;
const CPF_CHECK_DEBOUNCE_MS = 400;

const BATCH_HINT_BY_PREFIX: Record<string, string> = {
	author: 'João Souza;Maria dos Santos;...',
	kwrd: 'Administrativo;Licitações;...',
	'area-interest': 'Direito Constitucional;Direito Administrativo;...',
	theme: 'Governança;Compliance;...',
	event: 'FCGP 2018;ENGEP 2019;...',
};

export const SearchTagInsertModal: FC<IProps> = ({
	visible,
	close,
	insert_success,
	prefix,
	tag_name_in_portuguese,
}) => {
	const isAuthor = prefix === 'author';

	const [tag_title, set_tag_title] = useState('');
	const [has_cpf, set_has_cpf] = useState(false);
	const [cpf, set_cpf] = useState('');
	const [cpf_check_loading, set_cpf_check_loading] = useState(false);
	const [cpf_conflict, set_cpf_conflict] = useState<{
		title: string;
	} | null>(null);
	const [loading, set_loading] = useState(false);
	const cpf_debounce_ref = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (!visible) {
			set_tag_title('');
			set_has_cpf(false);
			set_cpf('');
			set_cpf_conflict(null);
			set_cpf_check_loading(false);
		}
	}, [visible]);

	useEffect(() => {
		if (cpf_debounce_ref.current) clearTimeout(cpf_debounce_ref.current);
		set_cpf_conflict(null);

		if (!isAuthor || !has_cpf) return;
		const sanitized = sanitizeCpf(cpf);
		if (sanitized.length !== 11 || !isValidCpf(sanitized)) return;

		set_cpf_check_loading(true);
		cpf_debounce_ref.current = setTimeout(async () => {
			try {
				const existing = await findSearchTagByCpf(sanitized);
				if (existing) set_cpf_conflict({ title: existing.title });
			} catch (err) {
				console.error('Erro ao verificar CPF:', err);
			} finally {
				set_cpf_check_loading(false);
			}
		}, CPF_CHECK_DEBOUNCE_MS);

		return () => {
			if (cpf_debounce_ref.current) clearTimeout(cpf_debounce_ref.current);
		};
	}, [cpf, isAuthor, has_cpf]);

	const sanitizedCpf = sanitizeCpf(cpf);
	const cpfFilled = sanitizedCpf.length > 0;
	const cpfFormatValid = sanitizedCpf.length === 11 && isValidCpf(sanitizedCpf);
	const cpfBlocking = isAuthor && has_cpf && (!cpfFormatValid || !!cpf_conflict);

	const submit = async (dto: InsertSearchTagDto) => {
		if (!dto.title) return;

		if (isAuthor && has_cpf) {
			const name = dto.title.trim();
			if (!name) {
				message.warning('Digite o nome do autor.');
				return;
			}
			if (!TAG_REGEX.test(name)) {
				message.error(`Nome inválido: ${name}`);
				return;
			}
			if (!cpfFormatValid) {
				message.error('Informe um CPF válido.');
				return;
			}
			if (cpf_conflict) {
				message.error(`Já existe um autor cadastrado com este CPF: ${cpf_conflict.title}.`);
				return;
			}

			set_loading(true);
			const outcome = await submitWithOverwriteConfirm(createSearchTag, {
				title: name,
				prefix: dto.prefix,
				cpf: sanitizedCpf,
			});
			set_loading(false);

			if (outcome.kind === 'ok') {
				message.success(`Autor "${name}" criado com sucesso!`);
				insert_success();
				close();
				return;
			}
			if (outcome.kind === 'cancelled') {
				message.info(`Cadastro de "${name}" cancelado.`);
				return;
			}
			const err: any = outcome.error;
			const status = err?.response?.status;
			const code = err?.response?.data?.code;
			if (status === 409 && code === 'cpf-conflict') {
				const existing = err?.response?.data?.existing;
				message.error(`Já existe um autor cadastrado com este CPF: ${existing?.title || ''}.`);
			} else {
				console.error('Erro ao criar autor:', err);
				message.error('Erro ao criar autor.');
			}
			return;
		}

		const tags = dto.title
			.split(';')
			.map((t) => t.trim())
			.filter((t) => t.length > 0);

		if (tags.length === 0) {
			message.warning('Digite pelo menos uma tag válida.');
			return;
		}

		const invalidTags = tags.filter((tag) => !TAG_REGEX.test(tag));
		if (invalidTags.length > 0) {
			message.error(`As seguintes tags são inválidas: ${invalidTags.join(', ')}`);
			return;
		}

		set_loading(true);
		try {
			for (const tag of tags) {
				const outcome = await submitWithOverwriteConfirm(createSearchTag, {
					title: tag,
					prefix: dto.prefix,
				});
				if (outcome.kind === 'ok') {
					message.success(`Tag "${tag}" criada com sucesso!`);
				} else if (outcome.kind === 'cancelled') {
					message.info(`Cadastro de "${tag}" cancelado.`);
				} else {
					const err: any = outcome.error;
					const status = err?.response?.status;
					const code = err?.response?.data?.code;
					if (status === 409 && code === 'cpf-conflict') {
						const existing = err?.response?.data?.existing;
						message.error(
							`"${tag}" já cadastrado com este CPF: ${existing?.title || ''}.`
						);
					} else {
						console.error(`Erro ao criar tag "${tag}":`, err);
						message.error(`Erro ao criar a tag "${tag}".`);
					}
				}
			}
			insert_success();
			close();
		} finally {
			set_loading(false);
		}
	};

	const submitDisabled = isAuthor
		? !tag_title || cpfBlocking || cpf_check_loading || loading
		: !tag_title || loading;

	return (
		<Modal
			{...{
				visible,
				centered: true,
				closable: true,
				destroyOnClose: true,
				onCancel: () => close(),
				title: `Criar ${tag_name_in_portuguese}`,
				footer: [
					<AddButton
						{...{
							onClick: () => submit({ title: tag_title, prefix }),
							disabled: submitDisabled,
							loading,
						}}
					/>,
				],
			}}
		>
			{isAuthor && (
				<div className='mt-2 mb-4'>
					<label className='mr-4 font-semibold'>Você possui o CPF do autor que deseja cadastrar?</label>
					<div className='mt-2'>
						<Radio.Group
							value={has_cpf ? 'yes' : 'no'}
							onChange={(e) => set_has_cpf(e.target.value === 'yes')}
						>
							<Radio value='no'>Não</Radio>
							<Radio value='yes'>Sim</Radio>
						</Radio.Group>
					</div>
				</div>
			)}

			<InputItem
				{...{
					label: isAuthor && has_cpf ? 'Nome do autor' : 'Título',
					on_changed: set_tag_title,
					input_item_name: tag_title,
				}}
			/>

			{isAuthor && has_cpf && (
				<div className='mt-4'>
					<label className='mr-4 font-semibold'>
						<span className='text-red-500'> * </span>CPF:
					</label>
					<div className='w-5/6'>
						<Input
							{...{
								value: cpf,
								onChange: (event) => set_cpf(maskCpf(event.target.value)),
								placeholder: '000.000.000-00',
								maxLength: 14,
								allowClear: true,
							}}
						/>
					</div>
					<div className='mt-2 text-sm'>
						{cpf_check_loading && <span style={{ color: '#888' }}>Verificando CPF...</span>}
						{!cpf_check_loading && cpfFilled && !cpfFormatValid && (
							<span style={{ color: '#d4380d' }}>CPF inválido.</span>
						)}
						{!cpf_check_loading && cpf_conflict && (
							<span style={{ color: '#d4380d' }}>
								Já existe um autor cadastrado com este CPF: {cpf_conflict.title}.
							</span>
						)}
						{!cpf_check_loading && cpfFormatValid && !cpf_conflict && (
							<span style={{ color: '#52c41a' }}>CPF disponível.</span>
						)}
					</div>
				</div>
			)}

			{(!isAuthor || !has_cpf) && (
				<small style={{ color: '#888' }}>
					Digite um ou mais itens.
					<br />
					Para cadastrar vários de uma vez, separe com{' '}
					<b>ponto e vírgula (;)</b>.
					{BATCH_HINT_BY_PREFIX[prefix] && (
						<>
							<br />
							Ex: <i>{BATCH_HINT_BY_PREFIX[prefix]}</i>
						</>
					)}
				</small>
			)}
		</Modal>
	);
};
